import {
  buildFilterForField,
  buildSearchableFilter,
  getSearchColumnPath,
  isSearchableField,
} from '@/utils/column-type-helpers'

/**
 * Landing parameters an iframe host (Client Portal 2.0, the FBO shell) can put
 * on the microapp URL to open the hub pre-navigated:
 *
 *   ?tab=<stage code | client-requests | ats-placements | vms-placements>
 *   ?worker=<worker full name>
 *
 * `tab` beats the user's last tab (localStorage `wfh_active_tab_override` /
 * `wfh_active_stage`); `worker` lands the target grid scoped to that worker as
 * the dismissible "Showing: <worker>" pill.
 *
 * The PARAMS are read once and stripped from the URL, mirroring the `token`
 * handling in auth-context: they describe how to *land*, so left in place any
 * later remount of the iframe (or a shell re-navigation reusing the same src)
 * would snap the user back to the deep-linked tab long after they'd navigated
 * away from it.
 *
 * That's about the params' lifetime, not the navigation's. Landing on a tab is
 * a real tab change and persists exactly as clicking that tab would — see the
 * effect in workflow-hub.tsx.
 *
 * Call this from effects only, never during render: it mutates history. The
 * memo is filled on first call rather than at import time so the strip happens
 * on the client, alongside auth-context's own replaceState — both re-read
 * `window.location.search` fresh at call time, so neither can resurrect the
 * other's already-stripped param regardless of which effect runs first.
 */
export interface DeepLinkParams {
  /** Raw `?tab=` value — a stage code or a Requests-group tab id. */
  tab: string | null
  /** Raw `?worker=` value — a worker full name, matched against WorkerFullName. */
  worker: string | null
}

const TAB_PARAM = 'tab'
const WORKER_PARAM = 'worker'

/**
 * The column every entity type exposes a worker's name on. `?worker=` resolves
 * against this code in the landing tab's own field configuration — tabs
 * without it land unfiltered (see adoptPendingSearchFilter in workflow-grid).
 */
export const WORKER_NAME_COLUMN_CODE = 'WorkerFullName'

/**
 * A run of anything that is not a letter, digit, `_` or apostrophe. The
 * apostrophe stays in so "O'Brien" survives as one word, which is how the
 * analyzer indexes it; `\p{L}` keeps accented and non-Latin names whole.
 */
const NON_WORD_RUN = /[^\p{L}\p{N}_']+/gu

const EMPTY: DeepLinkParams = { tab: null, worker: null }

let cached: DeepLinkParams | null = null
let applied = false

/**
 * Whether the landing params have already been acted on in this document.
 *
 * Module-level on purpose. `cached` outlives every component, so a guard held
 * in a component ref lets a remount of WorkflowHub read the cached values and
 * navigate the user back to the deep-linked tab — re-stashing the worker scope
 * with it — long after they'd moved on.
 *
 * Scope, precisely: this covers the TAB half, whose only caller is the effect
 * in workflow-hub.tsx. The stage half (`resolveInitialStage` in the context)
 * is deliberately not guarded — see the note there. Neither path is reachable
 * as the app is hosted today: the microapp lives in an iframe and every
 * navigation is a fresh document, which resets this module along with
 * everything else. This is belt-and-braces, not a fix for an observed bug.
 *
 * Note this marks the link handled WITHOUT clearing `cached`, deliberately.
 * Effects run child-before-parent, so WorkflowHub's effect fires before the
 * provider's bootstrap on the commit where stages arrive — clearing here would
 * empty the cache before `resolveInitialStage` had read the stage half of the
 * same link, silently dropping `?tab=<stage code>`.
 */
export function isDeepLinkApplied(): boolean {
  return applied
}

export function markDeepLinkApplied(): void {
  applied = true
}

function parseFromUrl(): DeepLinkParams {
  const params = new URLSearchParams(window.location.search)
  return {
    tab: (params.get(TAB_PARAM) ?? '').trim() || null,
    worker: (params.get(WORKER_PARAM) ?? '').trim() || null,
  }
}

/**
 * Read the params WITHOUT consuming them — no URL rewrite, no memo write, so
 * this is safe to call during render. That's what lets the hub know on its
 * very FIRST paint that a tab link is pending, and hold the shell rather than
 * painting the user's previous tab and then replacing it a beat later.
 *
 * Keeps returning the values after readDeepLinkParams has stripped them, so a
 * later render still sees the link.
 */
export function peekDeepLinkParams(): DeepLinkParams {
  if (cached) return cached
  if (typeof window === 'undefined') return EMPTY
  return parseFromUrl()
}

export function readDeepLinkParams(): DeepLinkParams {
  if (cached) return cached
  // SSR / non-browser: return without memoizing, so the first real client call
  // still gets to read and strip the URL.
  if (typeof window === 'undefined') return EMPTY

  const { tab, worker } = parseFromUrl()
  const params = new URLSearchParams(window.location.search)

  if (params.has(TAB_PARAM) || params.has(WORKER_PARAM)) {
    params.delete(TAB_PARAM)
    params.delete(WORKER_PARAM)
    const search = params.toString()
    const cleanUrl = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`
    try {
      window.history.replaceState(null, '', cleanUrl)
    } catch {
      // History unavailable (sandboxed frame) — the params stay on the URL.
      // Harmless: consumers still apply them once via their own guards.
    }
  }

  cached = { tab, worker }
  return cached
}

/**
 * Resolve a `?worker=` name into an OData clause against ONE tab's own field
 * configuration — which is why this runs in the adopting grid rather than
 * where the param is read: the reader only has the root WorkOrder config and
 * can't type another tab's WorkerFullName field.
 *
 * Returns null when the tab has no such column, and the caller must then land
 * unfiltered: filtering on a field the tab's index doesn't have is a 400 from
 * Azure Search, i.e. an error state instead of rows.
 *
 * `?worker=` is the one value here a host controls, so it is reduced to words
 * BEFORE it reaches either builder. Both emit Lucene syntax
 * (`search.ismatch(…, 'full', …)`) and neither escapes for Lucene, so a name
 * carrying `"` or `/` would reach the query as syntax and Azure Search would
 * 400 — the very error the null-guard above exists to prevent.
 *
 * Reducing rather than escaping is also what MATCHES: the index-side analyzer
 * discards this punctuation, so no indexed term contains it, and a clause
 * demanding one can never match. That is the same reasoning the dash note on
 * `buildSearchableFilter` already makes. Keeping it local means the shared
 * builder — used by every column-filter popover and the View Designer — is not
 * touched by this feature.
 *
 * The trade-off is a contains-match rather than the whole-value match the
 * worker-search modal uses — acceptable here, since that modal's value comes
 * from the index's own suggestions and this one cannot be trusted.
 */
export function buildWorkerScopeFilter(
  fieldsInUse: any[] | undefined | null,
  workerName: string,
): string | null {
  const workerField = (fieldsInUse ?? []).find((f: any) => f?.columnCode === WORKER_NAME_COLUMN_CODE)
  if (!workerField) return null

  if (isSearchableField(workerField)) {
    // Only this path needs reducing: the value lands inside a Lucene regex
    // literal, so a `"` or `/` would be read as syntax and 400 — and the
    // analyzer discards this punctuation anyway, so a clause demanding it
    // could never match. Written as a regex LITERAL on purpose: `\p{…}` is a
    // syntax error without the `u` flag, so dropping the flag fails the build
    // instead of silently turning the class into the literal characters
    // `p { L } N _ '` and letting quotes through again.
    const words = workerName.replace(NON_WORD_RUN, ' ').trim()
    if (!words) return null
    return buildSearchableFilter(words, getSearchColumnPath(workerField)) || null
  }

  // Non-searchable: a plain `WorkerFullName eq '<literal>'`, which
  // buildFilterForField already quote-escapes. Nothing parses it as Lucene, so
  // there is nothing to reduce for — and reducing would break the equality
  // outright, since "Jean-Pierre Roy" never equals "Jean Pierre Roy".
  return buildFilterForField(workerField, workerName)?.filter ?? null
}
